/* eslint-disable react-refresh/only-export-components */
import type { BuxusOperatorPayload } from '@buxus/shared'
import {
  FRIENDLY_CONNECTIVITY_MESSAGE,
  buildManifestScopeKey,
  catalogRowToDownloadInput,
  catalogOperatorNeedsDownload,
  countCatalogUpdatesAvailable,
  defaultIgnorableSasFetchError,
  executeOperatorDownload,
  extractManifestFileIds,
  findCatalogFileEntry,
  findCatalogOperatorRow,
  getOperatorDownloadTotalSteps,
  isConnectivityFailure,
  normalizeDownloadedOperatorJson,
  orderStakeholderCodes,
  persistedManifestFileIdsForOperator,
} from '@buxus/shared'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { OPERATOR_SESSION_STORAGE_KEY, PROFILE_STAKEHOLDER_STORAGE_KEY } from '../abp/config'
import { UnauthorizedHttpError } from '../abp/httpClient'
import { useAbp } from '../abp/react'
import { loadProfileJurisdictionCountyStateKeys } from '../auth/profileJurisdictionStorage'
import {
  fetchOperatorJsonFromSas,
  fetchOperatorsWithVersion,
  fetchSasBlob,
  pickOperatorSas,
  sortCatalogByDisplayName,
} from './operatorApi'
import {
  idbClearOperatorFiles,
  idbDeleteOperatorFiles,
  idbHasOperatorFile,
  idbPruneOperatorFiles,
  idbPutOperatorFile,
} from './operatorFileIdb'
import {
  idbClearOperatorPayloads,
  idbDeleteOperatorPayloads,
  idbGetBase,
  idbGetStakeholder,
  idbListStakeholderCodes,
  idbPutBase,
  idbPutStakeholder,
} from './operatorPayloadIdb'
import { resolveOperatorFileBlob } from './operatorFileResolve'
import {
  isExpiredSasFetchError,
  presentOperatorFileBlob,
  type OpenOperatorFileOptions,
} from './operatorFileOpen'
import {
  isWebErrorReported,
  leaveWebBreadcrumb,
} from '../errors/webErrorReporting'
import {
  isBuxusStorageFailure,
  reportLocalStorageError,
} from '../errors/webStorageErrorReporting'
import {
  reportOperationalFailure,
  reportOperatorManifestFileCachePartial,
  reportReliabilityFailure,
} from '../errors/webFailureReporting'
import type { OperatorWithVersionDto } from './types'

export type OpenOperatorFileParams = { fileId: string } & OpenOperatorFileOptions

/** Null means "do not apply stakeholder row filtering" in shared resolver. */
export const DEFAULT_STAKEHOLDER_CODE: string | null = null

export type PersistedOperatorEntry = {
  operatorId: string
  name: string
  cachedVersion: string
  /** Cached ABP tenancy name from catalog (URL segment source). */
  tenancyName?: string
  /** From catalog `logoImageSasUrl` — persisted so logos render before catalog refetch (hard reload). */
  logoImageSasUrl?: string | null
  /** Catalog `files[]` Zoho ids from last refresh — jurisdiction-filtered server-side. */
  manifestFileIds?: string[]
}

export type PersistedOperatorSession = {
  activeOperatorId: string | null
  stakeholderCode: string | null
  /**
   * User scope when `manifestFileIds` were snapshotted (registration stakeholder + jurisdictions).
   * Ignored when it does not match the current profile scope.
   */
  manifestScopeKey?: string | null
  entries: Record<string, PersistedOperatorEntry>
}

type StakeholderUrlsByOperator = Record<string, Record<string, string>>
type StakeholderPayloadByOperator = Record<string, Record<string, BuxusOperatorPayload>>

function currentManifestScopeKey(): string {
  let profileStakeholder: string | null = null
  try {
    profileStakeholder = localStorage.getItem(PROFILE_STAKEHOLDER_STORAGE_KEY)
  } catch {
    profileStakeholder = null
  }
  return buildManifestScopeKey({
    profileStakeholder,
    jurisdictionCountyStateKeys: loadProfileJurisdictionCountyStateKeys(),
  })
}

function snapshotManifestIdsFromCatalog(
  entries: Record<string, PersistedOperatorEntry>,
  catalog: OperatorWithVersionDto[],
): Record<string, PersistedOperatorEntry> {
  const next = { ...entries }
  for (const op of catalog) {
    const cur = next[op.operatorId]
    if (!cur) continue
    const ids = extractManifestFileIds(op as unknown as Record<string, unknown>)
    next[op.operatorId] = {
      ...cur,
      manifestFileIds: ids ? [...ids] : [],
    }
  }
  return next
}

function snapshotTenancyNamesFromCatalog(
  entries: Record<string, PersistedOperatorEntry>,
  catalog: OperatorWithVersionDto[] | null,
): Record<string, PersistedOperatorEntry> {
  if (!catalog?.length) return entries
  let next: Record<string, PersistedOperatorEntry> | null = null
  for (const op of catalog) {
    const tenancyName = op.tenancyName?.trim()
    if (!tenancyName) continue
    const cur = (next ?? entries)[op.operatorId]
    if (!cur || cur.tenancyName === tenancyName) continue
    if (!next) next = { ...entries }
    next[op.operatorId] = { ...cur, tenancyName }
  }
  return next ?? entries
}

const EMPTY_PERSISTED_SESSION: PersistedOperatorSession = {
  activeOperatorId: null,
  stakeholderCode: DEFAULT_STAKEHOLDER_CODE,
  entries: {},
}

const DOWNLOAD_NOTICE_ONE =
  "This operator couldn't be downloaded. Please try again later."
const DOWNLOAD_NOTICE_MANY = (count: number) =>
  `${count} operators couldn't be downloaded. Please try again later.`
const STORAGE_NOTICE_ONE =
  "This operator couldn't be saved on this device. Please try again later."
const STORAGE_NOTICE_MANY = (count: number) =>
  `${count} operators couldn't be saved on this device. Please try again later.`

export type DownloadOperatorOptions = {
  /** When true, caller sets the page notice after a batch; row failure state still updates. */
  batch?: boolean
}

function savePersisted(s: PersistedOperatorSession): void {
  try {
    localStorage.setItem(OPERATOR_SESSION_STORAGE_KEY, JSON.stringify(s))
  } catch (error) {
    reportLocalStorageError(error, {
      operation: 'setItem',
      key: OPERATOR_SESSION_STORAGE_KEY,
    })
  }
}

function hasPersistedDownloadedOperators(): boolean {
  return Object.keys(loadPersisted().entries).length > 0
}

function loadPersisted(): PersistedOperatorSession {
  try {
    const raw = localStorage.getItem(OPERATOR_SESSION_STORAGE_KEY)
    if (!raw) {
      return { ...EMPTY_PERSISTED_SESSION }
    }
    const parsed = JSON.parse(raw) as PersistedOperatorSession
    if (!parsed.entries || typeof parsed.entries !== 'object') {
      throw new Error('bad entries')
    }
    const base: PersistedOperatorSession = {
      activeOperatorId: typeof parsed.activeOperatorId === 'string' ? parsed.activeOperatorId : null,
      stakeholderCode:
        typeof parsed.stakeholderCode === 'string' && parsed.stakeholderCode.trim().length > 0
          ? parsed.stakeholderCode
          : DEFAULT_STAKEHOLDER_CODE,
      entries: parsed.entries,
    }
    return base
  } catch {
    return { ...EMPTY_PERSISTED_SESSION }
  }
}


type OperatorSessionValue = {
  catalog: OperatorWithVersionDto[] | null
  catalogLoading: boolean
  catalogError: string | null
  /** Count of catalog operators not downloaded or with a newer version (for Download tab badge). */
  catalogUpdateBadgeCount: number
  refreshCatalog: (options?: { blocking?: boolean }) => Promise<void>
  persisted: PersistedOperatorSession
  activePayload: BuxusOperatorPayload | null
  activeOperatorId: string | null
  stakeholderCode: string | null
  /** Updates persisted stakeholder only; App reloads payload via ensureOperatorPayload. */
  setStakeholderCode: (operatorId: string, code: string) => void
  clearStakeholderCode: () => void
  getAvailableStakeholderCodes: (operatorId: string | null) => string[]
  ensureOperatorPayload: (
    operatorId: string,
    stakeholderCodeForView?: string | null,
  ) => Promise<BuxusOperatorPayload>
  /** Payload already in memory (for per-operator nav pruning without selecting that operator). */
  getCachedOperatorPayload: (operatorId: string) => BuxusOperatorPayload | null
  downloadedOperators: PersistedOperatorEntry[]
  downloadOperator: (
    op: OperatorWithVersionDto,
    options?: DownloadOperatorOptions,
  ) => Promise<'ok' | 'storage' | 'download'>
  downloadAllOperators: () => Promise<void>
  clearDownloadedOperators: () => Promise<void>
  clearDownloadedOperator: (operatorId: string) => Promise<void>
  downloadAllBusy: boolean
  /** One user-facing message per download action (single or Download All summary). */
  downloadNotice: string | null
  /** Per-operator row failed state for iconic hint on the Download list. */
  downloadFailedOperatorIds: Readonly<Record<string, true>>
  /** In-memory SAS map for stakeholder switcher (operator users); repopulated when catalog is refreshed. */
  stakeholderUrlsByOperator: StakeholderUrlsByOperator
  setActiveOperatorId: (operatorId: string | null) => void
  downloadingId: string | null
  /** Per-operator download steps (done/total) for multi-blob downloads; empty when idle. */
  downloadProgressByOperator: Record<string, { done: number; total: number }>
  /** Fetch a catalog attachment by file id and open in browser tab or download. */
  openOperatorFile: (operatorId: string, params: OpenOperatorFileParams) => Promise<void>
  /** False until IndexedDB stakeholder code lists finish hydrating for downloaded operators. */
  /** Snapshot of catalog manifest ids for list UI before in-memory catalog returns (scope-checked). */
  getPersistedManifestFileIds: (operatorId: string) => ReadonlySet<string> | undefined
  stakeholderCodesHydrated: boolean
}

const OperatorSessionContext = createContext<OperatorSessionValue | null>(null)

export function OperatorSessionProvider({ children }: { children: ReactNode }) {
  const { ready, isAuthenticated } = useAbp()

  const [catalog, setCatalog] = useState<OperatorWithVersionDto[] | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(() => !hasPersistedDownloadedOperators())
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [persisted, setPersisted] = useState<PersistedOperatorSession>(() => loadPersisted())
  const [payloadCache, setPayloadCache] = useState<Record<string, BuxusOperatorPayload>>({})
  const [stakeholderPayloadByOperator, setStakeholderPayloadByOperator] =
    useState<StakeholderPayloadByOperator>({})
  const [stakeholderUrlsByOperator, setStakeholderUrlsByOperator] =
    useState<StakeholderUrlsByOperator>({})
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [downloadProgressByOperator, setDownloadProgressByOperator] = useState<
    Record<string, { done: number; total: number }>
  >({})
  const [downloadAllBusy, setDownloadAllBusy] = useState(false)
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null)
  const [downloadFailedOperatorIds, setDownloadFailedOperatorIds] = useState<Record<string, true>>(
    {},
  )
  const [idbStakeholderCodesByOperator, setIdbStakeholderCodesByOperator] = useState<
    Record<string, string[]>
  >({})
  const [stakeholderCodesHydrated, setStakeholderCodesHydrated] = useState(
    () => !hasPersistedDownloadedOperators(),
  )

  const catalogRefreshSeqRef = useRef(0)
  const catalogRefreshInFlightRef = useRef<Promise<void> | null>(null)
  const catalogRef = useRef(catalog)
  catalogRef.current = catalog
  const persistedRef = useRef(persisted)
  persistedRef.current = persisted

  const catalogUpdateBadgeCount = useMemo(() => {
    const cachedVersionByOperatorId: Record<string, string> = {}
    for (const [id, entry] of Object.entries(persisted.entries)) {
      cachedVersionByOperatorId[id] = entry.cachedVersion
    }
    return countCatalogUpdatesAvailable(catalog, cachedVersionByOperatorId)
  }, [catalog, persisted.entries])

  const downloadedIdsKey = useMemo(
    () => Object.keys(persisted.entries).sort().join('|'),
    [persisted.entries],
  )

  useEffect(() => {
    if (!isAuthenticated) {
      setIdbStakeholderCodesByOperator({})
      setStakeholderCodesHydrated(false)
      return
    }
    const ids = Object.keys(persisted.entries)
    if (ids.length === 0) {
      setIdbStakeholderCodesByOperator({})
      setStakeholderCodesHydrated(true)
      return
    }
    setStakeholderCodesHydrated(false)
    let cancelled = false
    void (async () => {
      const next: Record<string, string[]> = {}
      for (const id of ids) {
        try {
          next[id] = await idbListStakeholderCodes(id)
        } catch {
          next[id] = []
        }
      }
      if (!cancelled) {
        setIdbStakeholderCodesByOperator(next)
        setStakeholderCodesHydrated(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, downloadedIdsKey, persisted.entries])

  useEffect(() => {
    if (!ready) return

    if (isAuthenticated) {
      // Re-hydrate after sign-in or dev reload while tokens remain valid.
      setPersisted(loadPersisted())
      return
    }

    // Not authenticated: clear in-memory UI state only. Downloaded operator payloads
    // stay in localStorage/IndexedDB until explicit logout (purgeClientAuthStorage).
    setCatalog(null)
    setCatalogError(null)
    setStakeholderUrlsByOperator({})
    setCatalogLoading(!hasPersistedDownloadedOperators())
    setPersisted({ ...EMPTY_PERSISTED_SESSION })
    setPayloadCache({})
    setStakeholderPayloadByOperator({})
    setDownloadProgressByOperator({})
  }, [ready, isAuthenticated])

  const refreshCatalog = useCallback(async (_options?: { blocking?: boolean }) => {
    if (catalogRefreshInFlightRef.current) {
      return catalogRefreshInFlightRef.current
    }

    const run = async (): Promise<void> => {
      const seq = ++catalogRefreshSeqRef.current
      const hadCatalogList = !!(catalogRef.current && catalogRef.current.length > 0)
      setCatalogLoading(true)
      setCatalogError(null)
      setDownloadNotice(null)
      try {
        const list = await fetchOperatorsWithVersion()
        if (seq !== catalogRefreshSeqRef.current) return

        setCatalog(list)
        catalogRef.current = list
        const map: StakeholderUrlsByOperator = {}
        for (const op of list) {
          if (op.allStakeholderSas && typeof op.allStakeholderSas === 'object') {
            map[op.operatorId] = { ...op.allStakeholderSas }
          }
        }
        setStakeholderUrlsByOperator(map)
        /** Merge catalog metadata into persisted entries (logos, manifest ids, tenancy names). */
        setPersisted((prev) => {
          let nextEntries = snapshotManifestIdsFromCatalog(prev.entries, list)
          let logosChanged = false
          for (const op of list) {
            const url = op.logoImageSasUrl?.trim()
            if (!url) continue
            const cur = nextEntries[op.operatorId]
            if (!cur) continue
            if (cur.logoImageSasUrl !== url) {
              nextEntries = { ...nextEntries, [op.operatorId]: { ...cur, logoImageSasUrl: url } }
              logosChanged = true
            }
          }
          const entriesWithTenancy = snapshotTenancyNamesFromCatalog(nextEntries, list)
          const tenancyChanged = entriesWithTenancy !== nextEntries
          nextEntries = entriesWithTenancy
          const manifestScopeKey = currentManifestScopeKey()
          const manifestChanged =
            prev.manifestScopeKey !== manifestScopeKey ||
            Object.keys(prev.entries).some((id) => {
              const a = prev.entries[id]?.manifestFileIds ?? []
              const b = nextEntries[id]?.manifestFileIds ?? []
              return a.length !== b.length || a.some((x, i) => x !== b[i])
            })
          if (!logosChanged && !manifestChanged && !tenancyChanged) return prev
          const next: PersistedOperatorSession = {
            ...prev,
            manifestScopeKey,
            entries: nextEntries,
          }
          savePersisted(next)
          return next
        })
      } catch (e) {
        if (seq !== catalogRefreshSeqRef.current) return

        if (e instanceof UnauthorizedHttpError) {
          // Session cleared + login redirect handled in `httpClient` / `AuthSessionFailureBridge`.
          setCatalog([])
        } else {
          reportOperationalFailure(e, {
            context: 'refreshCatalog',
            metadata: {
              catalog: { hadCachedList: hadCatalogList },
            },
          })
          const message = isConnectivityFailure(e)
            ? FRIENDLY_CONNECTIVITY_MESSAGE
            : e instanceof Error
              ? e.message
              : String(e)
          if (hadCatalogList) {
            setCatalogError(
              'Could not refresh the operator list. Showing the last loaded list.',
            )
          } else {
            setCatalogError(message)
            setCatalog([])
          }
        }
      } finally {
        if (seq === catalogRefreshSeqRef.current) {
          setCatalogLoading(false)
        }
      }
    }

    const promise = run().finally(() => {
      if (catalogRefreshInFlightRef.current === promise) {
        catalogRefreshInFlightRef.current = null
      }
    })
    catalogRefreshInFlightRef.current = promise
    return promise
  }, [])

  useEffect(() => {
    if (!ready || !isAuthenticated) return
    const blocking = !hasPersistedDownloadedOperators()
    void refreshCatalog({ blocking })
  }, [ready, isAuthenticated, refreshCatalog])

  const activePayload = useMemo(() => {
    const id = persisted.activeOperatorId
    if (!id) return null
    return payloadCache[id] ?? null
  }, [persisted.activeOperatorId, payloadCache])

  const getAvailableStakeholderCodes = useCallback(
    (operatorId: string | null) => {
      if (!operatorId) return []
      const fromUrls = stakeholderUrlsByOperator[operatorId]
      const fromPayloads = stakeholderPayloadByOperator[operatorId]
      const fromIdb = idbStakeholderCodesByOperator[operatorId] ?? []
      const set = new Set<string>([
        ...Object.keys(fromUrls ?? {}),
        ...Object.keys(fromPayloads ?? {}),
        ...fromIdb,
      ])
      /** Order is applied in the shell via `orderStakeholderCodes` (canonical POC order). */
      return [...set]
    },
    [stakeholderUrlsByOperator, stakeholderPayloadByOperator, idbStakeholderCodesByOperator],
  )

  const ensureOperatorPayload = useCallback(
    async (
      operatorId: string,
      stakeholderCodeForView: string | null = null,
    ): Promise<BuxusOperatorPayload> => {
      try {
        const op = catalog?.find((x) => x.operatorId === operatorId)
        const expectedVersion = persisted.entries[operatorId]?.cachedVersion ?? op?.currentVersion
        if (!expectedVersion) {
          throw new Error(`Operator ${operatorId} is not in the current catalog. Refresh Downloads.`)
        }

        if (stakeholderCodeForView) {
          const memSh = stakeholderPayloadByOperator[operatorId]?.[stakeholderCodeForView]
          if (memSh) return memSh

          const fromIdb = await idbGetStakeholder(
            operatorId,
            stakeholderCodeForView,
            expectedVersion,
          )
          if (fromIdb) {
            setStakeholderPayloadByOperator((prev) => ({
              ...prev,
              [operatorId]: { ...prev[operatorId], [stakeholderCodeForView]: fromIdb },
            }))
            return fromIdb
          }

          const urls = stakeholderUrlsByOperator[operatorId]
          const sas = urls?.[stakeholderCodeForView]
          if (!sas) {
            throw new Error(
              `No stakeholder SAS for "${stakeholderCodeForView}"; refresh the operator list and download again.`,
            )
          }
          setDownloadingId(operatorId)
          try {
            const json = await fetchOperatorJsonFromSas(sas)
            const payload = normalizeDownloadedOperatorJson(json)
            setStakeholderPayloadByOperator((prev) => ({
              ...prev,
              [operatorId]: { ...prev[operatorId], [stakeholderCodeForView]: payload },
            }))
            await idbPutStakeholder(operatorId, stakeholderCodeForView, expectedVersion, payload)
            return payload
          } finally {
            setDownloadingId(null)
          }
        }

        const memBase = payloadCache[operatorId]
        if (memBase) return memBase

        const fromIdbBase = await idbGetBase(operatorId, expectedVersion)
        if (fromIdbBase) {
          setPayloadCache((prev) => ({ ...prev, [operatorId]: fromIdbBase }))
          return fromIdbBase
        }

        if (!op) {
          throw new Error(`Operator ${operatorId} is not in the current catalog. Refresh Downloads.`)
        }
        const sas = pickOperatorSas(op)
        if (!sas) {
          throw new Error(`No SAS URL for operator ${op.name} (${op.operatorId})`)
        }
        setDownloadingId(operatorId)
        try {
          const json = await fetchOperatorJsonFromSas(sas)
          const payload = normalizeDownloadedOperatorJson(json)
          setPayloadCache((prev) => ({ ...prev, [operatorId]: payload }))
          return payload
        } finally {
          setDownloadingId(null)
        }
      } catch (error) {
        reportOperationalFailure(error, {
          context: 'ensureOperatorPayload',
          metadata: {
            operatorPayload: {
              operatorId,
              stakeholderCode: stakeholderCodeForView,
            },
          },
        })
        throw error
      }
    },
    [
      catalog,
      payloadCache,
      stakeholderPayloadByOperator,
      stakeholderUrlsByOperator,
      persisted.entries,
    ],
  )

  const getCachedOperatorPayload = useCallback(
    (operatorId: string): BuxusOperatorPayload | null => payloadCache[operatorId] ?? null,
    [payloadCache],
  )

  const downloadedOperators = useMemo(
    () =>
      Object.values(persisted.entries).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [persisted.entries],
  )

  const downloadOperator = useCallback(
    async (
      op: OperatorWithVersionDto,
      options?: DownloadOperatorOptions,
    ): Promise<'ok' | 'storage' | 'download'> => {
      const batch = options?.batch === true
      const freshOp =
        catalogRef.current?.find((o) => o.operatorId === op.operatorId) ?? op
      const input = catalogRowToDownloadInput(freshOp as unknown as Record<string, unknown>)
      const { operatorId: id, currentVersion: version, name } = input
      const stakeholderMap =
        freshOp.allStakeholderSas && typeof freshOp.allStakeholderSas === 'object'
          ? (freshOp.allStakeholderSas as Record<string, string>)
          : null

      if (!batch) {
        setDownloadNotice(null)
      }
      setDownloadFailedOperatorIds((prev) => {
        if (!prev[id]) return prev
        const next = { ...prev }
        delete next[id]
        return next
      })

      const totalSteps = getOperatorDownloadTotalSteps(input, orderStakeholderCodes)
      setDownloadProgressByOperator((p) => ({ ...p, [id]: { done: 0, total: totalSteps } }))

      leaveWebBreadcrumb('downloadOperator started', {
        operatorId: id,
        name,
        currentVersion: version,
        totalSteps,
        manifestFileCount: input.manifestFiles.length,
        stakeholderCodeCount: Object.keys(input.stakeholderSasByCode).length,
      })

      let downloadSettled = false
      try {
        const {
          basePayload,
          stakeholderPayloads,
          manifestFileFailureCount,
          manifestFileFailureSampleMessage,
          manifestFileFailureSampleDomExceptionName,
        } = await executeOperatorDownload(input, {
          fetchOperatorJson: fetchOperatorJsonFromSas,
          fetchFileBlob: (sasUrl) => fetchSasBlob(sasUrl, { ignored: true }),
          putBase: idbPutBase,
          putStakeholder: idbPutStakeholder,
          putFile: async (opId, opVersion, file, blob) => {
            await idbPutOperatorFile(opId, opVersion, file.id, file.ext, blob, {
              ignored: true,
            })
          },
          hasCachedFile: (opId, opVersion, fileId) =>
            idbHasOperatorFile(opId, fileId, opVersion, { ignored: true }),
          pruneFilesNotInManifest: (opId, keep) =>
            idbPruneOperatorFiles(opId, keep, { ignored: true }),
          normalizePayload: normalizeDownloadedOperatorJson,
          orderStakeholderKeys: orderStakeholderCodes,
          isIgnorableSasError: defaultIgnorableSasFetchError,
          onProgress: (done, total) => {
            if (downloadSettled) return
            setDownloadProgressByOperator((p) => ({ ...p, [id]: { done, total } }))
          },
        })

        if (manifestFileFailureCount > 0) {
          reportOperatorManifestFileCachePartial({
            operatorId: id,
            name,
            currentVersion: version,
            manifestFileCount: input.manifestFiles.length,
            manifestFileFailureCount,
            sampleErrorMessage: manifestFileFailureSampleMessage,
            sampleDomExceptionName: manifestFileFailureSampleDomExceptionName,
          })
        }
        setStakeholderPayloadByOperator((prev) => ({
          ...prev,
          [id]: { ...prev[id], ...stakeholderPayloads },
        }))
        setPayloadCache((prev) => ({ ...prev, [id]: basePayload }))

        setPersisted((prev) => {
          const prevEntry = prev.entries[id]
          const logoUrl =
            (typeof freshOp.logoImageSasUrl === 'string' && freshOp.logoImageSasUrl.trim()) ||
            input.logoImageSasUrl?.trim() ||
            prevEntry?.logoImageSasUrl ||
            undefined
          const manifestIds = extractManifestFileIds(
            freshOp as unknown as Record<string, unknown>,
          )
          const manifestScopeKey = currentManifestScopeKey()
          const tenancyName = freshOp.tenancyName?.trim() || undefined
          const mergedEntries: Record<string, PersistedOperatorEntry> = {
            ...prev.entries,
            [id]: {
              operatorId: id,
              name,
              cachedVersion: version,
              ...(tenancyName ? { tenancyName } : {}),
              ...(logoUrl ? { logoImageSasUrl: logoUrl } : {}),
              manifestFileIds: manifestIds ? [...manifestIds] : [],
            },
          }
          const entries = snapshotTenancyNamesFromCatalog(mergedEntries, catalogRef.current)
          const next: PersistedOperatorSession = {
            ...prev,
            manifestScopeKey,
            entries,
          }
          savePersisted(next)
          return next
        })

        const freshStakeholderMap =
          freshOp.allStakeholderSas && typeof freshOp.allStakeholderSas === 'object'
            ? (freshOp.allStakeholderSas as Record<string, string>)
            : stakeholderMap
        if (freshStakeholderMap) {
          setStakeholderUrlsByOperator((m) => ({
            ...m,
            [id]: { ...freshStakeholderMap },
          }))
        }
        leaveWebBreadcrumb('downloadOperator completed', { operatorId: id, currentVersion: version })
        return 'ok'
      } catch (error) {
        if (!isWebErrorReported(error)) {
          reportReliabilityFailure(error, {
            context: 'downloadOperator',
            metadata: {
              download: {
                operatorId: id,
                name,
                currentVersion: version,
                totalSteps,
                manifestFileCount: input.manifestFiles.length,
              },
            },
          })
        }
        const failureKind = isBuxusStorageFailure(error) ? 'storage' : 'download'
        setDownloadFailedOperatorIds((prev) => ({ ...prev, [id]: true }))
        if (!batch) {
          setDownloadNotice(
            failureKind === 'storage' ? STORAGE_NOTICE_ONE : DOWNLOAD_NOTICE_ONE,
          )
        }
        return failureKind
      } finally {
        downloadSettled = true
        setDownloadProgressByOperator((p) => {
          const next = { ...p }
          delete next[id]
          return next
        })
      }
    },
    [catalog],
  )

  const downloadAllOperators = useCallback(async () => {
    if (!catalog?.length) return
    const cachedVersionByOperatorId: Record<string, string | undefined> = {}
    for (const [id, entry] of Object.entries(persisted.entries)) {
      cachedVersionByOperatorId[id] = entry.cachedVersion
    }
    setDownloadNotice(null)
    setDownloadAllBusy(true)
    leaveWebBreadcrumb('downloadAllOperators started', {
      catalogCount: catalog.length,
    })
    let failureCount = 0
    let storageFailureCount = 0
    try {
      const sorted = sortCatalogByDisplayName(catalog) ?? []
      for (const op of sorted) {
        if (!catalogOperatorNeedsDownload(op, cachedVersionByOperatorId)) continue
        const result = await downloadOperator(op, { batch: true })
        if (result === 'ok') continue
        failureCount += 1
        if (result === 'storage') storageFailureCount += 1
      }
      if (failureCount === 1) {
        setDownloadNotice(
          storageFailureCount === 1 ? STORAGE_NOTICE_ONE : DOWNLOAD_NOTICE_ONE,
        )
      } else if (failureCount > 1) {
        setDownloadNotice(
          storageFailureCount === failureCount
            ? STORAGE_NOTICE_MANY(failureCount)
            : DOWNLOAD_NOTICE_MANY(failureCount),
        )
      }
      leaveWebBreadcrumb('downloadAllOperators completed', {
        failureCount,
        storageFailureCount,
      })
    } finally {
      setDownloadAllBusy(false)
    }
  }, [catalog, downloadOperator, persisted.entries])

  const clearDownloadedOperators = useCallback(async () => {
    setDownloadProgressByOperator({})
    setDownloadNotice(null)
    setDownloadFailedOperatorIds({})
    setPersisted((prev) => {
      const next: PersistedOperatorSession = {
        ...prev,
        activeOperatorId: null,
        stakeholderCode: DEFAULT_STAKEHOLDER_CODE,
        entries: {},
      }
      savePersisted(next)
      return next
    })
    setPayloadCache({})
    setStakeholderPayloadByOperator({})
    setIdbStakeholderCodesByOperator({})
    await idbClearOperatorPayloads().catch(() => {
      /* ignore */
    })
    await idbClearOperatorFiles().catch(() => {
      /* ignore */
    })
  }, [])

  const clearDownloadedOperator = useCallback(
    async (operatorId: string) => {
      setDownloadFailedOperatorIds((prev) => {
        if (!prev[operatorId]) return prev
        const next = { ...prev }
        delete next[operatorId]
        return next
      })
      setDownloadProgressByOperator((p) => {
        const next = { ...p }
        delete next[operatorId]
        return next
      })
      setPersisted((prev) => {
        const nextEntries = { ...prev.entries }
        delete nextEntries[operatorId]
        const next: PersistedOperatorSession = {
          ...prev,
          activeOperatorId: prev.activeOperatorId === operatorId ? null : prev.activeOperatorId,
          entries: nextEntries,
        }
        savePersisted(next)
        return next
      })
      setPayloadCache((prev) => {
        const next = { ...prev }
        delete next[operatorId]
        return next
      })
      setStakeholderPayloadByOperator((prev) => {
        const next = { ...prev }
        delete next[operatorId]
        return next
      })
      setIdbStakeholderCodesByOperator((prev) => {
        const next = { ...prev }
        delete next[operatorId]
        return next
      })
      await idbDeleteOperatorPayloads(operatorId).catch(() => {
        /* ignore */
      })
      await idbDeleteOperatorFiles(operatorId).catch(() => {
        /* ignore */
      })
    },
    [catalog],
  )

  const setStakeholderCode = useCallback((_operatorId: string, code: string) => {
    setPersisted((prev) => {
      const next: PersistedOperatorSession = {
        ...prev,
        stakeholderCode: code,
      }
      savePersisted(next)
      return next
    })
  }, [])

  const clearStakeholderCode = useCallback(() => {
    setPersisted((prev) => {
      const next: PersistedOperatorSession = {
        ...prev,
        stakeholderCode: DEFAULT_STAKEHOLDER_CODE,
      }
      savePersisted(next)
      return next
    })
  }, [])

  const setActiveOperatorId = useCallback((operatorId: string | null) => {
    setPersisted((prev) => {
      const next: PersistedOperatorSession = { ...prev, activeOperatorId: operatorId }
      savePersisted(next)
      return next
    })
  }, [])

  const openOperatorFile = useCallback(
    async (operatorId: string, params: OpenOperatorFileParams) => {
      const { fileId, fileExt, fileName, displayName } = params
      const operatorVersion = persistedRef.current.entries[operatorId]?.cachedVersion ?? null

      const presentOpts = {
        fileId,
        fileExt: fileExt?.trim() ? fileExt : undefined,
        fileName: fileName?.trim(),
        displayName: displayName?.trim(),
        viewTab: params.viewTab,
      }

      const openFromBlob = async (blob: Blob, catalogExt?: string) => {
        await presentOperatorFileBlob(blob, {
          ...presentOpts,
          catalogExt,
        })
      }

      const tryOpen = async (afterRefresh: boolean): Promise<void> => {
        let blob = await resolveOperatorFileBlob(
          operatorId,
          operatorVersion,
          fileId,
          fileExt,
          catalogRef.current as Record<string, unknown>[] | null,
        )

        if (!blob && !afterRefresh) {
          const row = findCatalogOperatorRow(
            catalogRef.current as Record<string, unknown>[] | null,
            operatorId,
          )
          const entry = findCatalogFileEntry(row, fileId, fileExt)
          if (!entry) {
            throw new Error('FILE_NOT_IN_CATALOG')
          }
          await refreshCatalog()
          blob = await resolveOperatorFileBlob(
            operatorId,
            operatorVersion,
            fileId,
            fileExt,
            catalogRef.current as Record<string, unknown>[] | null,
          )
        }

        if (blob) {
          const row = findCatalogOperatorRow(
            catalogRef.current as Record<string, unknown>[] | null,
            operatorId,
          )
          const entry = findCatalogFileEntry(row, fileId, fileExt)
          await openFromBlob(blob, entry?.ext)
          return
        }

        const row = findCatalogOperatorRow(
          catalogRef.current as Record<string, unknown>[] | null,
          operatorId,
        )
        if (!row) {
          throw new Error('FILE_NOT_IN_CATALOG')
        }
        throw new Error('FILE_NOT_IN_CATALOG')
      }

      try {
        await tryOpen(false)
      } catch (e) {
        if (isExpiredSasFetchError(e)) {
          await refreshCatalog()
          await tryOpen(true)
          return
        }
        if (e instanceof Error && e.message === 'FILE_NOT_IN_CATALOG') throw e
        reportOperationalFailure(e, {
          context: 'openOperatorFile',
          metadata: {
            operatorFile: { operatorId, fileId },
          },
        })
        throw e
      }
    },
    [refreshCatalog],
  )

  const getPersistedManifestFileIds = useCallback(
    (operatorId: string) =>
      persistedManifestFileIdsForOperator(persisted, operatorId, currentManifestScopeKey()),
    [persisted],
  )

  const value = useMemo<OperatorSessionValue>(
    () => ({
      catalog,
      catalogLoading,
      catalogError,
      catalogUpdateBadgeCount,
      refreshCatalog,
      persisted,
      activePayload,
      activeOperatorId: persisted.activeOperatorId,
      stakeholderCode: persisted.stakeholderCode,
      setStakeholderCode,
      clearStakeholderCode,
      getAvailableStakeholderCodes,
      ensureOperatorPayload,
      getCachedOperatorPayload,
      downloadedOperators,
      downloadOperator,
      downloadAllOperators,
      clearDownloadedOperators,
      clearDownloadedOperator,
      downloadAllBusy,
      downloadNotice,
      downloadFailedOperatorIds,
      stakeholderUrlsByOperator,
      setActiveOperatorId,
      downloadingId,
      downloadProgressByOperator,
      openOperatorFile,
      getPersistedManifestFileIds,
      stakeholderCodesHydrated,
    }),
    [
      catalog,
      catalogLoading,
      catalogError,
      catalogUpdateBadgeCount,
      refreshCatalog,
      persisted,
      activePayload,
      setStakeholderCode,
      clearStakeholderCode,
      getAvailableStakeholderCodes,
      ensureOperatorPayload,
      getCachedOperatorPayload,
      downloadedOperators,
      downloadOperator,
      downloadAllOperators,
      clearDownloadedOperators,
      clearDownloadedOperator,
      downloadAllBusy,
      downloadNotice,
      downloadFailedOperatorIds,
      stakeholderUrlsByOperator,
      setActiveOperatorId,
      downloadingId,
      downloadProgressByOperator,
      openOperatorFile,
      getPersistedManifestFileIds,
      stakeholderCodesHydrated,
    ],
  )

  return <OperatorSessionContext.Provider value={value}>{children}</OperatorSessionContext.Provider>
}

export function useOperatorSession(): OperatorSessionValue {
  const v = useContext(OperatorSessionContext)
  if (!v) throw new Error('useOperatorSession must be used within OperatorSessionProvider')
  return v
}
